import { Component, inject, Input, input } from '@angular/core';
import { fmtDate } from 'src/app/_helpers/utils';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import {
  getCancellationFlowStatusClass,
  getCancellationFlowStatusLabel,
  isCancellationFlowDeletable,
  isCancellationFlowOver,
} from 'src/app/enums/cancellation/cancellation-flow-status.enum';
import { CancellationCancelModalComponent } from 'src/app/shared/components/cancellation-cancel-modal/cancellation-cancel-modal.component';
import { CancellationDeleteModalComponent } from 'src/app/shared/components/cancellation-delete-modal/cancellation-delete-modal.component';
import { CancellationDetailsService } from 'src/app/shared/components/cancellation-details/cancellation-details.service';
import { CancellationRequestInfoComponent } from 'src/app/shared/components/cancellation-details/cancellation-request-info.component';
import { CancellationEditModalComponent } from 'src/app/shared/components/cancellation-edit-modal/cancellation-edit-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { TaskDetailsService } from '../../task-details.service';
import { TaskCancellationsService } from './task-cancellations.service';

/**
 * One cancellation of the opened flow: its request, corrected in place as on
 * its own sheet. A cancellation that is over reads the same, with nothing left
 * to act on. The running one's letter and replacement sit above the stepper,
 * its documents in « Documents contractuels », its log in the affaire's.
 *
 * Without write access the card reads the same, with no action.
 *
 * The details come with the affaire's batch. The request info reads its own
 * mediator, so each card provides one, seeded with the batch's copy.
 */
@Component({
  selector: 'app-task-cancellation-card',
  standalone: true,
  imports: [
    ExpansionPanelComponent,
    IconsModule,
    CancellationRequestInfoComponent,
    CancellationEditModalComponent,
    CancellationCancelModalComponent,
    CancellationDeleteModalComponent,
  ],
  providers: [CancellationDetailsService],
  template: `
    <app-expansion-panel [title]="title(details)" [collapsed]="collapsed">
      <!-- Direct children of the panel, hidden rather than removed: see the request info's « Modifier ». -->
      <span
        expansionPanelHeaderSuffix
        class="lty-badge {{ statusClass(details.status) }}"
        >{{ statusLabel(details.status) }}</span
      >
      <!-- Correctable while running, as on the sheet: the same rule as calling it off. -->
      <button
        expansionPanelHeaderActions
        type="button"
        class="lty-btn lty-btn-white lty-btn-sm"
        [hidden]="!callable"
        (click)="editModal.openModal(details)"
        i18n
      >
        Modifier
      </button>
      <button
        expansionPanelHeaderActions
        type="button"
        class="lty-btn lty-btn-white lty-btn-sm"
        i18n-title
        title="Annuler la résiliation"
        i18n-aria-label
        aria-label="Annuler la résiliation"
        [hidden]="!callable"
        (click)="cancelModal.openModal(details.id)"
      >
        <app-cross-icon
          class="d-flex"
          height="16px"
          width="16px"
          color="var(--gray-500)"
        />
      </button>
      <button
        expansionPanelHeaderActions
        type="button"
        class="lty-btn lty-btn-white lty-btn-sm"
        i18n-title
        title="Supprimer cette résiliation"
        i18n-aria-label
        aria-label="Supprimer cette résiliation"
        [hidden]="!deletable"
        (click)="deleteModal.openModal(details.id)"
      >
        <app-trash-icon
          class="d-flex"
          height="16px"
          width="16px"
          color="var(--gray-500)"
        />
      </button>

      <app-cancellation-request-info [bare]="true" />
    </app-expansion-panel>

    <!-- The header's actions. The slide-in sits above the affaire's own sheet, which is the host's layer. -->
    <app-cancellation-edit-modal
      #editModal
      [layer]="layer() + 1"
      (changed)="store.changed($event)"
    />
    <app-cancellation-cancel-modal
      #cancelModal
      (changed)="store.settled($event)"
    />
    <app-cancellation-delete-modal #deleteModal (deleted)="store.settled()" />
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class TaskCancellationCardComponent {
  private readonly detailsService = inject(CancellationDetailsService);

  private readonly taskDetailsService = inject(TaskDetailsService);

  protected readonly store = inject(TaskCancellationsService);

  /** The batch's copy; a re-read hands the card a fresh one in place. */
  @Input({ required: true })
  set cancellation(details: CancellationFlowDetailsResponse) {
    // On the first arrival only, so a re-read keeps the card as the user left it.
    if (!this.details) this.collapsed = isCancellationFlowOver(details.status);
    this.details = details;
    this.detailsService.seed(details);
  }

  /** The host sheet's layer; the slide-in form opens one above it. */
  readonly layer = input(0);

  // Set before the first check: the input is required.
  protected details!: CancellationFlowDetailsResponse;

  /** Only the running one starts open. */
  protected collapsed = false;

  protected readonly statusLabel = getCancellationFlowStatusLabel;

  protected readonly statusClass = getCancellationFlowStatusClass;

  protected title(details: CancellationFlowDetailsResponse): string {
    return $localize`Demande de résiliation du ${fmtDate(details.createdOn)}:date:`;
  }

  /** As on the sheet: a request that is over stays as it ended, uncorrected. */
  protected get callable(): boolean {
    return this.canEdit && !isCancellationFlowOver(this.details.status);
  }

  /** As on the sheet: only a cancellation nobody has signed can be taken back. */
  protected get deletable(): boolean {
    return this.canEdit && isCancellationFlowDeletable(this.details.status);
  }

  private get canEdit(): boolean {
    return this.taskDetailsService.state.canEdit;
  }
}
